/**
 * CriarOrcamentoDialog — slide-over "Criar orcamento" do lead (Orcamento v2).
 *
 * Fiel ao mockup "Orcamento": recap do lead + dados que vao para o
 * cliente + toggle Parceiro/Indicacao (RT). Ao "Iniciar orçamento", cria
 * direto e navega para a pagina do orcamento (sem wizard intermediario —
 * decisao 2026-06-10 com a usuaria).
 *
 * Duas opcoes de contratante:
 *   1. "Usar contato do lead" — o backend cria o Cliente a partir do lead
 *      (Transition Form, CRM-14/18): o dialogo pergunta PF/PJ (PJ
 *      pre-selecionado quando o lead tem empresa) e exige nome completo e
 *      telefone ou e-mail; o que for digitado aqui e gravado no lead antes
 *      de criar o cliente. Lista EXATAMENTE o que a conversao copia e avisa
 *      quando falta e-mail ou CPF/CNPJ (CRM-41).
 *   2. Vincular a um Cliente ja cadastrado — busca com debounce. Lead que ja
 *      tinha outro cliente leva os orcamentos nao convertidos junto (CRM-19).
 *
 * Lead que ja tem cliente: o Cliente e a fonte do contato (CRM-11) — o
 * dialogo mostra o cliente vinculado, sem campos de contato.
 *
 * Parceiro/RT: o percentual eh SEMPRE herdado do cadastro do Representante
 * (regra F-D11) — aqui apenas exibimos o RT do selecionado.
 *
 * CSS: classes .orc2-panel (styles/canonical/orcamento-v2.css).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowRight, FiSearch, FiUsers } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarClientes, listarRepresentantes } from "../../../services/comercial";
import { CAMPO_GERAL, errosPorCampo } from "../../../utils/errosApi";
import { mascaraDocumento } from "../../../utils/documento";
import { capitalizarNome, mascaraTelefone } from "../../../utils/leadFormat";
import { rotuloDaCoincidencia } from "../../../components/AvisoContatoExistente";
import { cidadeUf, enderecoPrincipal } from "../enderecosLead";
import { Field, FormStack, InlineError, InlineHint, Input } from "../styles";
import { EmptyMsg } from "../../Orcamentos/styles";
import {
  PESSOA_FISICA,
  PESSOA_JURIDICA,
  dadosCopiadosNaConversao,
  tipoSugerido,
  validarContatoParaConversao,
} from "./contatoLead";

const TONE_TEMPERATURA = { QUENTE: "danger", MORNO: "warning", FRIO: "info" };

const MSG_SELECIONE_CLIENTE = "Selecione um cliente ou use o contato do lead.";
const MSG_SELECIONE_PARCEIRO = "Selecione o parceiro ou desative a indicação.";
const MSG_CONFIRA_CONTATO = "Confira os dados do contato.";
const HTTP_CONFLITO = 409;
const MSG_DOCUMENTO_REPETIDO =
  "Este CPF/CNPJ já pertence a um cliente da unidade: vincule o lead a ele ou corrija o documento.";

function iniciais(nome) {
  return (nome || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function contatoInicial(lead) {
  return {
    tipo: tipoSugerido(lead),
    nome: lead?.contato_nome || "",
    telefone: lead?.contato_telefone || "",
    email: lead?.contato_email || "",
    documento: lead?.documento ? mascaraDocumento(lead.documento) : "",
  };
}

/** Lista de candidatos do 409 (CRM-12): o vendedor vincula ou cria mesmo assim. */
function ClientesCandidatos({ conflito, onVincular, onCriarMesmoAssim }) {
  return (
    <div className="so-banner" role="alert" data-testid="conflito-cliente-existente">
      <strong>
        {conflito.documento_repetido ? MSG_DOCUMENTO_REPETIDO : conflito.detalhe}
      </strong>
      <ul style={{ margin: "6px 0 0", paddingLeft: 16, display: "grid", gap: 6 }}>
        {conflito.candidatos.map((c) => (
          <li key={c.id}>
            {[c.codigo, c.nome, c.documento ? mascaraDocumento(c.documento) : "", c.telefone, c.email]
              .filter(Boolean)
              .join(" · ")}
            {" "}({rotuloDaCoincidencia(c.coincide)}){" "}
            <AsyncButton
              variant="secondary"
              onClick={() => onVincular(c)}
              silentSuccess
              silentError
            >
              Vincular a este cliente
            </AsyncButton>
          </li>
        ))}
      </ul>
      {!conflito.documento_repetido && (
        <div style={{ marginTop: 8 }}>
          <AsyncButton variant="secondary" onClick={onCriarMesmoAssim} silentSuccess silentError>
            Criar mesmo assim (é outra pessoa)
          </AsyncButton>
        </div>
      )}
    </div>
  );
}

function ErroDoCampo({ campo, erros }) {
  if (!erros?.[campo]) return null;
  return <InlineError role="alert">{erros[campo]}</InlineError>;
}

function CriarOrcamentoDialog({ open, onClose, onCriar, lead, contextoSpin }) {
  const { request } = useAuth();
  const { leadId } = useParams();
  const navigate = useNavigate();
  // modo: "contato" (usa contato do lead) | "cliente" (vincula existente)
  const [modo, setModo] = useState("contato");
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [buscando, setBuscando] = useState(false);
  // Erros por campo ({contato_nome, contato_telefone, tipo, __nonfield__}).
  const [erros, setErros] = useState({});
  // 409 do backend: `{detalhe, candidatos, documento_repetido}` (CRM-12).
  const [conflito, setConflito] = useState(null);
  // Contato que vai para o cliente (Transition Form).
  const [contato, setContato] = useState(() => contatoInicial(lead));
  // Parceiro / Indicacao (RT)
  const [temParceiro, setTemParceiro] = useState(false);
  const [representantes, setRepresentantes] = useState([]);
  const [parceiroSel, setParceiroSel] = useState("");

  useEffect(() => {
    if (open) {
      setModo("contato");
      setBusca("");
      setResultados([]);
      setClienteSel(null);
      setErros({});
      setContato(contatoInicial(lead));
      setTemParceiro(false);
      setParceiroSel("");
    }
  }, [open, lead]);

  // Representantes carregados na primeira ativacao do toggle (lazy).
  useEffect(() => {
    if (!temParceiro || representantes.length) return;
    listarRepresentantes(request, { pageSize: 100 })
      .then((p) => {
        const items = Array.isArray(p) ? p : p?.results || [];
        setRepresentantes(items);
      })
      .catch(() => setRepresentantes([]));
  }, [temParceiro, representantes.length, request]);

  // Busca de clientes com debounce simples.
  useEffect(() => {
    if (modo !== "cliente") return undefined;
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      return undefined;
    }
    let ativo = true;
    setBuscando(true);
    const id = setTimeout(() => {
      listarClientes(request, { busca: termo, pageSize: 8 })
        .then((p) => {
          if (!ativo) return;
          const items = Array.isArray(p) ? p : p?.results || [];
          setResultados(items);
        })
        .catch(() => ativo && setResultados([]))
        .finally(() => ativo && setBuscando(false));
    }, 350);
    return () => {
      ativo = false;
      clearTimeout(id);
    };
  }, [busca, modo, request]);

  const repSel = representantes.find((r) => String(r.id) === String(parceiroSel));
  const clienteVinculado = lead.cliente_resumo?.id ? lead.cliente_resumo : null;

  const setCampoContato = (campo, valor) =>
    setContato((prev) => ({ ...prev, [campo]: valor }));
  // Normalizacao no blur, nunca a cada tecla (ADR-0059).
  const normalizarNoBlur = (campo, fn) =>
    setContato((prev) => ({ ...prev, [campo]: fn(prev[campo]) }));

  // Erro lancado e engolido pelo AsyncButton (silentError): o aviso fica no
  // campo ou no rodape do dialogo, sem toast de sucesso.
  const recusar = (errosNovos, mensagem) => {
    setErros(errosNovos);
    throw new Error(mensagem);
  };

  const montarPayload = () => {
    if (modo === "cliente") {
      if (!clienteSel) recusar({ [CAMPO_GERAL]: MSG_SELECIONE_CLIENTE }, MSG_SELECIONE_CLIENTE);
      return { cliente_id: clienteSel.id };
    }
    if (clienteVinculado) return {};
    const errosContato = validarContatoParaConversao(contato, lead);
    if (Object.keys(errosContato).length) recusar(errosContato, MSG_CONFIRA_CONTATO);
    return {
      tipo: contato.tipo,
      contato_nome: contato.nome.trim(),
      contato_telefone: contato.telefone.trim(),
      contato_email: contato.email.trim(),
      documento: contato.documento.trim(),
    };
  };

  const enviar = async (payload) => {
    let orcamento;
    try {
      orcamento = await onCriar(payload);
    } catch (err) {
      if (err?.status === HTTP_CONFLITO && Array.isArray(err.payload?.candidatos)) {
        setConflito({
          detalhe: err.payload.detalhe || "",
          candidatos: err.payload.candidatos,
          documento_repetido: Boolean(err.payload.documento_repetido),
        });
        return;
      }
      setErros(errosPorCampo(err));
      throw err;
    }
    setConflito(null);
    if (orcamento?.id) {
      navigate(`/orcamentos/${orcamento.id}`, {
        state: { from: `/crm/leads/${leadId}` },
      });
    }
  };

  const criar = async () => {
    setErros({});
    setConflito(null);
    const payload = montarPayload();
    if (temParceiro) {
      if (!parceiroSel) recusar({ [CAMPO_GERAL]: MSG_SELECIONE_PARCEIRO }, MSG_SELECIONE_PARCEIRO);
      payload.parceiros = [{ representante: parceiroSel }];
    }
    await enviar(payload);
  };

  const comParceiro = (payload) =>
    temParceiro && parceiroSel ? { ...payload, parceiros: [{ representante: parceiroSel }] } : payload;
  // "Vincular a este cliente": o contato do dialogo nao vai — o Cliente e a fonte (CRM-11).
  const vincularCandidato = (candidato) => enviar(comParceiro({ cliente_id: candidato.id }));
  const criarMesmoAssim = () => enviar(comParceiro({ ...montarPayload(), criar_mesmo_assim: true }));

  const contatoLabel =
    lead.contato_nome || lead.cliente_nome || lead.titulo || "contato do lead";
  const nomeCompleto = [lead.contato_nome, lead.contato_sobrenome]
    .filter(Boolean)
    .join(" ") || contatoLabel;
  const toneTemp = TONE_TEMPERATURA[lead.temperatura] || "neutral";
  // O Lead nao tem cidade/bairro soltos: tudo vem do endereco principal
  // (ADR-0071), que tambem e o que a conversao copia para o cliente.
  const principal = enderecoPrincipal(lead);
  const copia = dadosCopiadosNaConversao(lead, contato);
  const pj = contato.tipo === PESSOA_JURIDICA;

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Criar orçamento"
      subtitle={`${lead.numero || ""} · gerenciando lead`}
      width="min(95vw, 560px)"
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancelar
          </button>
          <AsyncButton onClick={criar} silentSuccess silentError>
            Iniciar orcamento <FiArrowRight size={14} aria-hidden="true" />
          </AsyncButton>
        </>
      }
    >
      <div className="orc2-panel">
        <FormStack>
          <div className="tabchip-group">
            <div
              className={"tabchip" + (modo === "contato" ? " active" : "")}
              onClick={() => setModo("contato")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setModo("contato")}
            >
              {clienteVinculado ? "Cliente do lead" : "Usar contato do lead"}
            </div>
            <div
              className={"tabchip" + (modo === "cliente" ? " active" : "")}
              onClick={() => setModo("cliente")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setModo("cliente")}
            >
              {clienteVinculado ? "Vincular outro cliente" : "Vincular cliente existente"}
            </div>
          </div>

          {modo === "contato" ? (
            <>
              <div className="lead-recap">
                <div className="lav">{iniciais(nomeCompleto)}</div>
                <div className="li">
                  <div className="n">{nomeCompleto}</div>
                  <div className="s">
                    {[lead.titulo, cidadeUf(principal)].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                {lead.temperatura && (
                  <span className={`badge ${toneTemp} no-dot`}>
                    Lead {String(lead.temperatura_display || lead.temperatura).toLowerCase()}
                  </span>
                )}
              </div>

              {clienteVinculado ? (
                <>
                  <div className="so-banner" role="status">
                    O orçamento sai para o cliente já vinculado:{" "}
                    <strong>{clienteVinculado.nome}</strong>
                    {clienteVinculado.codigo ? ` · ${clienteVinculado.codigo}` : ""}.
                    Nome, telefone e e-mail são os da ficha do cliente.
                  </div>
                  <dl className="lead-kv">
                    <dt>Telefone</dt>
                    <dd>{clienteVinculado.telefone || "—"}</dd>
                    <dt>E-mail</dt>
                    <dd>{clienteVinculado.email || "—"}</dd>
                    <dt>CPF/CNPJ</dt>
                    <dd>{clienteVinculado.documento || "—"}</dd>
                    <dt>Interesse</dt>
                    <dd>{lead.titulo || "—"}</dd>
                  </dl>
                  {copia.avisos.map((aviso) => (
                    <InlineHint key={aviso} role="status">
                      {aviso}
                    </InlineHint>
                  ))}
                </>
              ) : (
                <>
                  <div className="so-section-label">Contratante</div>
                  <div
                    role="radiogroup"
                    aria-label="Tipo de cliente"
                    style={{ display: "flex", gap: 16 }}
                  >
                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="radio"
                        name="tipo-cliente"
                        value={PESSOA_FISICA}
                        checked={!pj}
                        onChange={() => setCampoContato("tipo", PESSOA_FISICA)}
                      />
                      Pessoa física
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="radio"
                        name="tipo-cliente"
                        value={PESSOA_JURIDICA}
                        checked={pj}
                        onChange={() => setCampoContato("tipo", PESSOA_JURIDICA)}
                      />
                      Pessoa jurídica
                    </label>
                  </div>
                  {pj && (
                    <InlineHint>
                      O cliente será a empresa <strong>{lead.empresa || "—"}</strong>; a
                      pessoa abaixo entra como contato dela.
                    </InlineHint>
                  )}
                  <ErroDoCampo campo="tipo" erros={erros} />

                  <Field>
                    Nome completo
                    <Input
                      type="text"
                      value={contato.nome}
                      onChange={(e) => setCampoContato("nome", e.target.value)}
                      onBlur={() => normalizarNoBlur("nome", capitalizarNome)}
                      placeholder="Nome completo da pessoa"
                      maxLength={200}
                    />
                    <ErroDoCampo campo="contato_nome" erros={erros} />
                  </Field>
                  <Field>
                    Telefone
                    <Input
                      type="text"
                      value={contato.telefone}
                      onChange={(e) => setCampoContato("telefone", e.target.value)}
                      onBlur={() => normalizarNoBlur("telefone", mascaraTelefone)}
                      placeholder="(00) 00000-0000"
                      maxLength={30}
                    />
                    <ErroDoCampo campo="contato_telefone" erros={erros} />
                  </Field>
                  <Field>
                    E-mail
                    <Input
                      type="email"
                      value={contato.email}
                      onChange={(e) => setCampoContato("email", e.target.value)}
                      placeholder="contato@email.com"
                      maxLength={200}
                    />
                    <ErroDoCampo campo="contato_email" erros={erros} />
                  </Field>
                  <Field>
                    {pj ? "CNPJ da empresa (ou CPF do contato)" : "CPF"}
                    <Input
                      type="text"
                      value={contato.documento}
                      onChange={(e) => setCampoContato("documento", e.target.value)}
                      onBlur={() => normalizarNoBlur("documento", (v) => mascaraDocumento(v))}
                      placeholder={pj ? "00.000.000/0000-00" : "000.000.000-00"}
                      maxLength={18}
                    />
                    <ErroDoCampo campo="documento" erros={erros} />
                  </Field>

                  <div className="so-section-label">Dados que vão para o cliente</div>
                  <dl className="lead-kv">
                    {copia.linhas.map((linha) => (
                      <React.Fragment key={linha.rotulo}>
                        <dt>{linha.rotulo}</dt>
                        <dd>{linha.valor}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                  {copia.avisos.map((aviso) => (
                    <InlineHint key={aviso} role="status">
                      {aviso}
                    </InlineHint>
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {clienteVinculado && (
                <div className="so-banner" role="status">
                  Os orçamentos ainda não convertidos deste lead passarão do cliente{" "}
                  <strong>{clienteVinculado.nome}</strong> para o cliente escolhido.
                </div>
              )}
              <Field>
                Buscar cliente
                <div className="input-prefixed">
                  <FiSearch size={14} aria-hidden="true" />
                  <input
                    type="search"
                    value={busca}
                    onChange={(e) => {
                      setBusca(e.target.value);
                      setClienteSel(null);
                    }}
                    placeholder="Nome, código, documento, e-mail..."
                    autoFocus
                  />
                </div>
              </Field>

              {clienteSel ? (
                <div className="so-banner">
                  Selecionado: <strong>{clienteSel.nome}</strong>
                  {clienteSel.documento ? ` · ${clienteSel.documento}` : ""}
                </div>
              ) : buscando ? (
                <EmptyMsg style={{ padding: 8 }}>Buscando...</EmptyMsg>
              ) : busca.trim().length >= 2 && resultados.length === 0 ? (
                <EmptyMsg style={{ padding: 8 }}>
                  Nenhum cliente encontrado para &quot;{busca.trim()}&quot;.
                </EmptyMsg>
              ) : (
                resultados.map((c) => (
                  <div
                    key={c.id}
                    className="client-pick"
                    role="button"
                    tabIndex={0}
                    onClick={() => setClienteSel(c)}
                    onKeyDown={(e) => e.key === "Enter" && setClienteSel(c)}
                  >
                    <div className="av">{iniciais(c.nome)}</div>
                    <div className="ci">
                      <div className="n">{c.nome}</div>
                      <div className="s">
                        {c.documento || c.tipo_display || c.tipo || "—"}
                        {c.cidade ? ` · ${c.cidade}` : ""}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          <div className="so-section-label">Parceiro / Indicacao (RT)</div>
          <div className="parceiro-toggle-row">
            <div className="pt-txt">
              <div className="t">Há um parceiro atrelado?</div>
              <div className="s">Arquiteto, designer ou indicação com RT</div>
            </div>
            <button
              type="button"
              className={"switch" + (temParceiro ? " is-on" : "")}
              role="switch"
              aria-checked={temParceiro}
              aria-label="Há um parceiro atrelado?"
              onClick={() => setTemParceiro((v) => !v)}
            />
          </div>

          {temParceiro && (
            <>
              <Field>
                Parceiro
                <select
                  className="select"
                  value={parceiroSel}
                  onChange={(e) => setParceiroSel(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {representantes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nome}
                    </option>
                  ))}
                </select>
              </Field>
              {repSel && (
                <div className="rt-preview">
                  <FiUsers size={14} aria-hidden="true" /> +
                  {Number(repSel.comissao_percentual || 0)}% de RT sobre o valor
                  final (herdado do cadastro do parceiro)
                </div>
              )}
            </>
          )}

          {contextoSpin && (
            <>
              <div className="so-section-label">Contexto SPIN</div>
              <div className="so-banner" style={{ whiteSpace: "pre-wrap" }}>
                {contextoSpin}
              </div>
            </>
          )}

          {conflito && (
            <ClientesCandidatos
              conflito={conflito}
              onVincular={vincularCandidato}
              onCriarMesmoAssim={criarMesmoAssim}
            />
          )}
          {erros[CAMPO_GERAL] && <InlineError role="alert">{erros[CAMPO_GERAL]}</InlineError>}
        </FormStack>
      </div>
    </SlideOverPanel>
  );
}

export default CriarOrcamentoDialog;
